/**
 * Transport abstraction for agent communication.
 *
 * Provides WebSocketTransport behind a shared AgentTransport interface.
 */

import type { AgentAction } from "../../shared/types/AgentAction";
import type { BaseAgentPrompt } from "../../shared/types/AgentPrompt";
import type { Streaming } from "../../shared/types/Streaming";
import { wsUrl } from "../api";
import { CANVAS_GATEWAY_BASE_URL } from "../../flags";
import { useTurnStore } from "@/features/tutor/store/useTurnStore";
import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useSessionStore } from "@/features/tutor/store/useSessionStore";
import {
  useNotepadStore,
  type CanvasConnectionStatus,
} from "@/features/notepad/store/useNotepadStore";
import { TOKEN_KEY } from "@/constants/auth";
import {
  CanvasChunkType,
  type CanvasChunk,
  type CanvasTextChunkData,
  type CanvasErrorChunkData,
  type CanvasMetricsChunkData,
  type CanvasSeedChunkData,
  makeCanvasChunk,
  isCanvasChunk,
} from "./chunk_protocol";
import * as pako from "pako";
import { debugLog, debugWarn } from "@/utils/debugLog";

/**
 * Compress obj as JSON using pako.deflate and send as a binary WebSocket frame.
 * pako.deflate produces zlib format (0x78 header) compatible with Python zlib.decompress().
 * Uint8Array is an ArrayBufferView — ws.send() accepts it directly, no .buffer needed.
 */
function sendCompressed(ws: WebSocket, obj: unknown): void {
  const json = JSON.stringify(obj);
  const compressed = pako.deflate(new TextEncoder().encode(json));
  ws.send(compressed);
}

type OutboundQueueItem = {
  obj: unknown;
  turnId: string | null;
};

function getOutboundTurnId(obj: unknown): string | null {
  if (!obj || typeof obj !== "object") return null;
  const record = obj as Record<string, unknown>;
  return typeof record.turn_id === "string" && record.turn_id.length > 0
    ? record.turn_id
    : null;
}

/**
 * Resolve the shared tutor/canvas turn id.
 * Canvas requests must use the same turn id namespace as tutor turns so stale
 * chunks are filtered consistently across both systems.
 */
function resolveSharedTurnId(): string {
  const store = useTurnStore.getState();
  const turnId = store?.getTurnId?.();
  if (typeof turnId === "string" && turnId.trim().length > 0) {
    return turnId;
  }

  const fallbackTurnId = crypto.randomUUID();
  store?.setTurnId?.(fallbackTurnId);
  return fallbackTurnId;
}

/**
 * Send a TURN_CHANGE chunk for canvas interruption.
 *
 * If the shared turn id already changed away from `previousTurnId`, this is a
 * no-op to avoid duplicate turn changes when another caller already rotated it.
 */
function sendCanvasTurnChangeOnAbort(
  ws: WebSocket,
  previousTurnId: string,
): void {
  if (ws.readyState !== WebSocket.OPEN) return;

  const store = useTurnStore.getState();
  const currentTurnId = store?.getTurnId?.();
  if (store.turnChangeFailed) return;
  if (
    typeof currentTurnId === "string" &&
    currentTurnId.trim().length > 0 &&
    currentTurnId !== previousTurnId
  ) {
    return;
  }

  const nextTurnId = crypto.randomUUID();
  store?.setTurnId?.(nextTurnId);
  sendCompressed(ws, {
    chunk_type: "TURN_CHANGE",
    turn_id: nextTurnId,
    timestamp: 0,
    chunk_data: JSON.stringify({ is_interrupt: true }),
  });
}

// ---------------------------------------------------------------------------
// AgentTransport interface
// ---------------------------------------------------------------------------

/**
 * Agent transport. Lets the canvas run on NoOpTransport when the agent is off.
 */
export interface AgentTransport {
  stream(
    prompt: BaseAgentPrompt,
    signal: AbortSignal,
  ): AsyncGenerator<Streaming<AgentAction>>;
  dispose(): void;
}

// ---------------------------------------------------------------------------
// WebSocketTransport
// ---------------------------------------------------------------------------

/**
 * WebSocket transport with persistent connection, auto-reconnect with
 * exponential backoff, cancel-via-message, and heartbeat ping/pong handling.
 *
 * The WebSocket connection is established eagerly in the constructor and
 * reused across multiple stream() calls.
 */
export class WebSocketTransport implements AgentTransport {
  private static MAX_RECONNECT_ATTEMPTS = 10;
  /** Max messages to hold while reconnecting. Oldest are dropped when full. */
  private static OUTBOUND_QUEUE_MAX = 20;

  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private hasOpened = false;
  private hasAcceptedSession = false;
  private disposed = false;
  private connectPromise: Promise<WebSocket | null> | null = null;
  private reconnectTimerId: ReturnType<typeof setTimeout> | null = null;
  private wsUrl = "";
  /** Messages buffered while the WS is not open — flushed on reconnect. */
  private outboundQueue: OutboundQueueItem[] = [];
  /** Callback fired when max reconnect attempts are exhausted. */
  onMaxReconnectsExhausted?: () => void;
  private owner = {};
  private unsubscribeSession: (() => void) | null = null;

  constructor() {
    useNotepadStore.getState().claimConnection(this.owner, () => this.retry());
    // Session loss and activity changes invalidate an established connection.
    const refresh = () => this.tryConnect();
    const unsubSession = useSessionStore.subscribe(refresh);
    const unsubSlide = useSlideStore.subscribe(refresh);
    this.unsubscribeSession = () => {
      unsubSession();
      unsubSlide();
    };
    this.tryConnect();
  }

  private ownsConnection(): boolean {
    return (
      !this.disposed &&
      useNotepadStore.getState().preprocessOwner === this.owner
    );
  }

  private setStatus(status: CanvasConnectionStatus): void {
    useNotepadStore.getState().setConnectionStatus(status, this.owner);
  }

  private closeSocket(): void {
    const ws = this.ws;
    this.ws = null;
    this.connectPromise = null;
    if (this.reconnectTimerId !== null) {
      clearTimeout(this.reconnectTimerId);
      this.reconnectTimerId = null;
    }
    if (ws) {
      useNotepadStore.getState().clearNotepadWs(ws);
      ws.close();
    }
  }

  /** Build every connection from the currently accepted session/activity. */
  private tryConnect(force = false): boolean {
    if (!this.ownsConnection()) return false;
    const lessonId = useSlideStore.getState().currentLessonId;
    const sessionId = useSessionStore.getState().getSessionId();
    if (!Number.isFinite(lessonId) || lessonId <= 0 || !sessionId) {
      this.closeSocket();
      this.wsUrl = "";
      this.outboundQueue = [];
      useNotepadStore.getState().resetPreprocess(this.owner);
      this.setStatus(this.hasAcceptedSession ? "session_lost" : "waiting");
      return false;
    }

    const token = localStorage.getItem(TOKEN_KEY);

    const params = new URLSearchParams();
    params.set("lessonId", String(lessonId));
    params.set("sessionId", sessionId);
    if (token) params.set("token", token);
    const tvOverride =
      import.meta.env.DEV || import.meta.env.VITE_ALLOW_TV_OVERRIDE === "true"
        ? new URLSearchParams(window.location.search).get("tv")
        : null;
    if (tvOverride) params.set("tv", tvOverride);
    if (useNotepadStore.getState().isNotepadBite) {
      const slideIndex = useSlideStore.getState().currentSlideIndex;
      if (slideIndex != null) params.set("slideIndex", String(slideIndex));
    }
    // Tells the gateway it may deliver paper OCR out-of-turn via CANVAS_INJECT.
    params.set("inject", "1");

    const url = wsUrl(`/canvas/ws?${params.toString()}`);
    const targetChanged = url !== this.wsUrl;
    if (!targetChanged && !force) return true;
    this.closeSocket();
    if (targetChanged) {
      this.outboundQueue = [];
      this.reconnectAttempts = 0;
      // Invalidate preparation before React replaces the board for this activity.
      this.owner = {};
      useNotepadStore
        .getState()
        .claimConnection(this.owner, () => this.retry());
    }
    this.wsUrl = url;
    this.hasAcceptedSession = true;
    this.setStatus(this.hasOpened ? "retrying" : "connecting");
    void this.healthCheck();
    // onclose handles failures before a stream awaits this eager connection.
    this.connectPromise = this.connect(url).catch(() => null);
    return true;
  }

  retry(): void {
    if (!this.ownsConnection()) return;
    this.reconnectAttempts = 0;
    this.tryConnect(true);
  }

  private releaseSessionWatch(): void {
    this.unsubscribeSession?.();
    this.unsubscribeSession = null;
  }

  // -- Connection management -----------------------------------------------

  /**
   * Lightweight health check against the gateway's HTTP /health endpoint.
   * If the gateway is unreachable the WS connect will fail anyway, but this
   * gives an earlier, clearer log message and lets us skip the WS attempt
   * entirely on hard failures (e.g. DNS resolution failure, gateway down).
   *
   * Resolves always (never rejects) — worst case we fall through and let
   * the WS connection attempt surface its own error.
   */
  private async healthCheck(): Promise<void> {
    if (this.disposed) return;
    const url = `${CANVAS_GATEWAY_BASE_URL}/health`;
    try {
      const res = await fetch(url, {
        method: "GET",
        signal: AbortSignal.timeout(3000),
      });
      if (!res.ok) {
        debugWarn(
          `[WebSocketTransport] Gateway health check returned ${res.status} — proceeding with WS connect anyway`,
        );
      }
    } catch (err) {
      debugWarn(
        "[WebSocketTransport] Gateway health check failed — gateway may be unreachable:",
        err,
      );
    }
  }

  private connect(url: string): Promise<WebSocket> {
    return new Promise<WebSocket>((resolve, reject) => {
      if (!this.ownsConnection()) {
        reject(new Error("Transport disposed"));
        return;
      }

      const ws = new WebSocket(url);
      ws.binaryType = "arraybuffer";

      // Track immediately so dispose() can close in-flight connections
      // that haven't fired onopen yet (prevents zombie WebSocket leaks).
      this.ws = ws;

      // Persistent ping/pong handler — always active while WS is open
      ws.addEventListener("message", (event: MessageEvent) => {
        if (!this.ownsConnection() || this.ws !== ws) return;
        let text: string | null = null;
        if (event.data instanceof ArrayBuffer) {
          try {
            const inflated = pako.inflate(new Uint8Array(event.data));
            text = new TextDecoder().decode(inflated);
          } catch {
            return; // Not a ping — let stream handler deal with it
          }
        } else if (typeof event.data === "string") {
          text = event.data;
        }
        if (!text) return;
        try {
          const msg = JSON.parse(text);
          if (msg && msg.type === "ping" && ws.readyState === WebSocket.OPEN) {
            sendCompressed(ws, { type: "pong" });
          }
          if (msg && msg.type === "server_shutdown") {
            // Gateway is restarting — dispatch event so the canvas panel can
            // show a banner. The WS will close and reconnect automatically.
            window.dispatchEvent(new CustomEvent("canvas:server-shutdown"));
          }
          // Handle CANVAS_AUDIO at connection level so audio plays even after
          // the per-stream onMessage listener is removed (CANVAS_AUDIO arrives
          // after CANVAS_DONE which tears down the stream listener).
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_AUDIO
          ) {
            const currentTurnId = useTurnStore.getState().getTurnId?.();
            if (
              typeof currentTurnId === "string" &&
              currentTurnId.length > 0 &&
              msg.turn_id !== currentTurnId
            ) {
              return;
            }
            window.dispatchEvent(
              new CustomEvent("canvas:audio-chunk", { detail: msg }),
            );
          }
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_DONE
          ) {
            window.dispatchEvent(
              new CustomEvent("canvas:turn-done", { detail: msg }),
            );
          }
          // Connection level: injects arrive outside turns, with no stream listener.
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_INJECT
          ) {
            window.dispatchEvent(
              new CustomEvent("canvas:inject-actions", { detail: msg }),
            );
          }
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_METRICS
          ) {
            const metrics = JSON.parse(
              msg.chunk_data,
            ) as CanvasMetricsChunkData;
            window.dispatchEvent(
              new CustomEvent("canvas:metrics", {
                detail: {
                  turnId: msg.turn_id,
                  metrics,
                },
              }),
            );
          }
          // Connection-level preprocessing chunks — sent by the gateway at WS
          // open with a synthetic turn id, before any turn exists. Buffered in
          // the notepad store; the seed applier and the opening "start
          // teaching" gate consume them from there.
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_SEED
          ) {
            try {
              const seedData = JSON.parse(
                msg.chunk_data,
              ) as CanvasSeedChunkData;
              if (
                Array.isArray(seedData.actions) &&
                seedData.actions.length > 0
              ) {
                useNotepadStore
                  .getState()
                  .setPendingSeedActions(seedData.actions, this.owner);
                debugLog(
                  "[WebSocketTransport] CANVAS_SEED received:",
                  seedData.actions.length,
                  "actions",
                );
              }
            } catch (err) {
              debugWarn("[WebSocketTransport] CANVAS_SEED parse failed:", err);
            }
          }
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_READY
          ) {
            let data: { state_version?: number; snapshot_revision?: number } =
              {};
            try {
              data = JSON.parse(msg.chunk_data) ?? {};
            } catch {
              // Older gateways can signal readiness without checkpoint tokens.
            }
            const sessionId = useSessionStore.getState().getSessionId();
            const slideIndex = useSlideStore.getState().currentSlideIndex;
            const validToken =
              typeof sessionId === "string" &&
              Number.isInteger(slideIndex) &&
              Number.isInteger(data.state_version) &&
              Number(data.state_version) >= 0 &&
              Number.isInteger(data.snapshot_revision) &&
              Number(data.snapshot_revision) >= 0;
            useNotepadStore.getState().setCheckpointToken(
              validToken
                ? {
                    sessionId,
                    slideIndex,
                    stateVersion: data.state_version!,
                    snapshotRevision: data.snapshot_revision!,
                  }
                : null,
              this.owner,
            );
            useNotepadStore.getState().setGatewayReady(this.owner);
            debugLog("[WebSocketTransport] CANVAS_READY received");
          }
          if (
            isCanvasChunk(msg) &&
            msg.chunk_type === CanvasChunkType.CANVAS_USER_TRANSCRIPT
          ) {
            debugLog("[WebSocketTransport] CANVAS_USER_TRANSCRIPT received:", {
              turnId: msg.turn_id,
              transcript: msg.chunk_data,
            });
            window.dispatchEvent(
              new CustomEvent("canvas:user-transcript", {
                detail: {
                  turnId: msg.turn_id,
                  transcript: msg.chunk_data,
                },
              }),
            );
          }
        } catch {
          // Not JSON — ignore
        }
      });

      ws.onopen = () => {
        if (!this.ownsConnection() || this.ws !== ws) {
          ws.close();
          reject(new Error("Transport disposed during connect"));
          return;
        }
        this.ws = ws;
        this.reconnectAttempts = 0;
        useNotepadStore.getState().setNotepadWs(ws);
        this.setStatus("open");
        this.flushOutboundQueue(ws);
        // A reopened socket is a fresh gateway session with none of the old one's state.
        if (this.hasOpened) {
          window.dispatchEvent(new CustomEvent("canvas:ws-reconnected"));
        }
        this.hasOpened = true;
        resolve(ws);
      };

      ws.onclose = () => {
        reject(new Error("WebSocket closed before opening"));
        useNotepadStore.getState().clearNotepadWs(ws);
        if (!this.ownsConnection() || this.ws !== ws) return;
        this.ws = null;
        this.connectPromise = null;
        this.scheduleReconnect();
      };

      ws.onerror = (event) => {
        if (!this.disposed) {
          debugWarn("[WebSocketTransport] Connection error", event);
        }
        // onclose will fire after onerror, so reconnect is handled there.
        // Reject the connect promise only if we haven't opened yet.
        if (ws.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket connection failed"));
        }
      };
    });
  }

  private scheduleReconnect(): void {
    if (!this.ownsConnection() || this.reconnectTimerId !== null) return;

    if (this.reconnectAttempts >= WebSocketTransport.MAX_RECONNECT_ATTEMPTS) {
      console.error(
        `[WebSocketTransport] Max reconnect attempts (${WebSocketTransport.MAX_RECONNECT_ATTEMPTS}) reached — giving up`,
      );
      this.setStatus("exhausted");
      this.onMaxReconnectsExhausted?.();
      return;
    }

    this.setStatus("retrying");
    const delay = this.getReconnectDelay();
    debugWarn(
      `[WebSocketTransport] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1}/${WebSocketTransport.MAX_RECONNECT_ATTEMPTS})`,
    );

    this.reconnectTimerId = setTimeout(() => {
      this.reconnectTimerId = null;
      if (!this.ownsConnection()) return;
      this.reconnectAttempts++;
      this.tryConnect(true);
    }, delay);
  }

  private getReconnectDelay(): number {
    const base = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
    // +/- 25% jitter
    const jitter = base * 0.25 * (2 * Math.random() - 1);
    return Math.round(base + jitter);
  }

  /**
   * Send a message, buffering it if the WS is not currently open.
   * Buffered messages are flushed automatically when the connection reopens.
   */
  private sendOrQueue(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      sendCompressed(this.ws, obj);
    } else {
      const item: OutboundQueueItem = {
        obj,
        turnId: getOutboundTurnId(obj),
      };
      // Drop oldest if queue is full to prevent unbounded growth
      if (this.outboundQueue.length >= WebSocketTransport.OUTBOUND_QUEUE_MAX) {
        this.outboundQueue.shift();
      }
      this.outboundQueue.push(item);
    }
  }

  private flushOutboundQueue(ws: WebSocket): void {
    while (this.outboundQueue.length > 0) {
      const item = this.outboundQueue.shift();
      if (!item) continue;
      const currentTurnId = useTurnStore.getState().getTurnId?.();
      if (
        item.turnId &&
        typeof currentTurnId === "string" &&
        currentTurnId.length > 0 &&
        item.turnId !== currentTurnId
      ) {
        continue;
      }
      if (ws.readyState === WebSocket.OPEN) {
        sendCompressed(ws, item.obj);
      }
    }
  }

  private async ensureConnected(): Promise<WebSocket> {
    if (!this.ownsConnection())
      throw new Error("Transport disposed or superseded");
    if (!this.tryConnect())
      throw new Error(
        "Canvas gateway connection is not available yet (no session or lesson)",
      );
    if (this.ws?.readyState === WebSocket.OPEN) return this.ws;
    if (!this.connectPromise) this.retry();
    const ws = await this.connectPromise;
    if (
      !ws ||
      !this.ownsConnection() ||
      this.ws !== ws ||
      ws.readyState !== WebSocket.OPEN
    ) {
      throw new Error(
        "Canvas gateway connection is unavailable; retry the connection",
      );
    }
    return ws;
  }

  // -- Streaming -----------------------------------------------------------

  async *stream(
    prompt: BaseAgentPrompt,
    signal: AbortSignal,
  ): AsyncGenerator<Streaming<AgentAction>> {
    // A cancelled turn must not reconnect a transport that cleanup has closed.
    if (signal.aborted) return;
    if (useTurnStore.getState().turnChangeFailed) {
      throw new Error(
        "Canvas turn blocked: tutor TURN_CHANGE was not delivered",
      );
    }
    const ws = await this.ensureConnected();
    if (signal.aborted || useTurnStore.getState().turnChangeFailed) return;
    let streamTurnId = "";

    // Async queue pattern: bridge onmessage callbacks to async generator
    type QueueItem =
      | { type: "data"; events: Streaming<AgentAction>[] }
      | { type: "end" }
      | { type: "error"; error: Error };

    const queue: QueueItem[] = [];
    let resolve: (() => void) | null = null;

    const enqueue = (item: QueueItem) => {
      queue.push(item);
      if (resolve) {
        resolve();
        resolve = null;
      }
    };

    // --- Audio-synced action scheduling ------------------------------------
    // Ops the notepad agent inlined in its narration arrive with `timestamp`:
    // ms from the start of this turn's audio, taken from the TTS alignment of
    // the word the op was anchored to. Hold each one until that point on the
    // audio clock so the canvas draws in step with what's being said — the
    // same scheduling the bite tutor applies to TUTOR_ACTION `action_time`.
    //
    // A FIFO drained one entry at a time keeps ops in authoring order even if
    // two land in the same tick. Ops released without an onset (the gateway's
    // OpTimingJoin.drain sends them with timestamp 0) go through the same queue
    // with no delay, so a turn that loses its alignment part-way still applies
    // its canvas work in authoring order. Untimed actions (message deltas and
    // the rest, which carry no `timestamp` at all) bypass it entirely.
    const timedQueue: Array<{ action: Streaming<AgentAction>; atMs: number }> =
      [];
    let timedTimer: ReturnType<typeof setTimeout> | null = null;
    // The entry whose timer is currently pending. Held here rather than only in
    // the timer closure so the error path can flush it — it is the *earliest*
    // scheduled op, and dropping it while applying its successors would apply a
    // `set`/`annotate` whose target was never added.
    let inFlight: { action: Streaming<AgentAction>; atMs: number } | null =
      null;
    let endAfterTimed = false;

    const drainTimedQueue = () => {
      const next = timedQueue.shift();
      if (!next) {
        timedTimer = null;
        if (endAfterTimed) {
          endAfterTimed = false;
          enqueue({ type: "end" });
        }
        return;
      }
      inFlight = next;
      const streamStart = window.__canvasStreamStartWallClock ?? 0;
      const delayMs = streamStart
        ? Math.max(0, streamStart + next.atMs - Date.now())
        : 0;
      timedTimer = setTimeout(() => {
        inFlight = null;
        enqueue({ type: "data", events: [next.action] });
        drainTimedQueue();
      }, delayMs);
    };

    /** Drop pending scheduled actions — the turn is over or was cancelled. */
    const clearTimedQueue = () => {
      if (timedTimer) clearTimeout(timedTimer);
      timedTimer = null;
      inFlight = null;
      timedQueue.length = 0;
      endAfterTimed = false;
    };

    /**
     * Apply everything still scheduled, in authoring order, right now. For when
     * the audio clock these were waiting on has become meaningless but the
     * canvas work is still wanted.
     */
    const flushTimedQueue = () => {
      const pending = inFlight
        ? [inFlight, ...timedQueue.splice(0)]
        : timedQueue.splice(0);
      clearTimedQueue();
      for (const item of pending) {
        enqueue({ type: "data", events: [item.action] });
      }
    };

    /**
     * End the stream, but not before scheduled actions have had their turn:
     * the gateway sends CANVAS_DONE as soon as TTS drains, which is earlier
     * than the last op's onset.
     */
    const enqueueEnd = () => {
      if (timedTimer || timedQueue.length > 0) {
        endAfterTimed = true;
        return;
      }
      enqueue({ type: "end" });
    };

    const waitForItem = (): Promise<void> => {
      if (queue.length > 0) return Promise.resolve();
      return new Promise<void>((r) => {
        resolve = r;
      });
    };

    /** Reject after `ms` of silence — clears itself if an item arrives first. */
    const waitForItemWithTimeout = (ms: number): Promise<void> => {
      return new Promise<void>((res, rej) => {
        const timer = setTimeout(() => {
          rej(new Error(`Canvas agent request timed out after ${ms / 1000}s`));
        }, ms);
        waitForItem().then(
          () => {
            clearTimeout(timer);
            res();
          },
          () => {
            clearTimeout(timer);
          },
        );
      });
    };

    // Use addEventListener (not ws.onmessage assignment) so multiple
    // concurrent streams don't overwrite each other's handlers.
    const onMessage = (event: MessageEvent) => {
      if (!this.ownsConnection() || this.ws !== ws) return;
      let text: string;

      if (event.data instanceof ArrayBuffer) {
        // Binary frame: decompress with pako (server sends zlib-compressed JSON)
        try {
          const inflated = pako.inflate(new Uint8Array(event.data));
          text = new TextDecoder().decode(inflated);
        } catch (err) {
          debugWarn(
            "[WebSocketTransport] Binary frame inflate failed, skipping",
            err,
          );
          return;
        }
      } else {
        text = typeof event.data === "string" ? event.data : "";
      }

      let parsed: unknown;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Non-JSON frames are ignored.
        parsed = null;
      }

      if (parsed && typeof parsed === "object") {
        const msg = parsed as Record<string, unknown>;

        // 1. Control messages (bare JSON) -- ping handled at connection level
        if (msg.type === "ping") return; // Handled by persistent listener
        if (msg.type === "stream_end") {
          enqueueEnd();
          return;
        }

        // 2. Chunk protocol messages
        if (isCanvasChunk(parsed)) {
          const chunk = parsed as CanvasChunk;

          // Drop chunks from stale/cancelled turns
          if (chunk.turn_id !== streamTurnId) {
            return;
          }

          switch (chunk.chunk_type) {
            case CanvasChunkType.CANVAS_START: {
              // Gateway started processing the turn — mirrors tutor START chunk.
              window.dispatchEvent(new CustomEvent("canvas:turn-start"));
              return;
            }
            case CanvasChunkType.CANVAS_ACTION: {
              const action = JSON.parse(
                chunk.chunk_data,
              ) as Streaming<AgentAction>;
              const atMs = (action as Record<string, unknown>).timestamp;
              // close / next_slide / end_lesson carry a timestamp too, but
              // their own ActionUtils already schedule against the audio
              // clock — queueing them here would delay them twice.
              //
              // `gesture` is NOT in this list on purpose. A pointer mark that
              // lands several sentences ahead of the narration circles the
              // wrong words, so it wants exactly this scheduling and does no
              // scheduling of its own.
              const selfScheduling =
                action._type === "close" ||
                action._type === "next_slide" ||
                action._type === "end_lesson";
              // `>= 0`, not `> 0`: an op the gateway released without an onset
              // arrives with 0 and must still queue behind ops already holding a
              // positive onset, or the recovery path applies later-authored ops
              // first. Message deltas carry no `timestamp` and are unaffected.
              if (typeof atMs === "number" && atMs >= 0 && !selfScheduling) {
                timedQueue.push({ action, atMs });
                if (!timedTimer) drainTimedQueue();
                return;
              }
              enqueue({ type: "data", events: [action] });
              return;
            }
            case CanvasChunkType.CANVAS_DONE: {
              enqueueEnd();
              return;
            }
            case CanvasChunkType.CANVAS_ERROR: {
              const errorData = JSON.parse(
                chunk.chunk_data,
              ) as CanvasErrorChunkData;
              if (
                errorData.error?.includes("TTS finalize idle timeout") ||
                errorData.error?.includes("TTS finalize timed out")
              ) {
                console.warn(
                  "[WebSocketTransport] TTS timeout (non-fatal):",
                  errorData.error,
                );
                enqueueEnd();
              } else {
                // Hard error: audio is gone, so the clock these were waiting
                // on is meaningless. Apply them now rather than dropping the
                // canvas work along with the turn.
                flushTimedQueue();
                enqueue({
                  type: "error",
                  error: new Error(errorData.error),
                });
              }
              return;
            }
            case CanvasChunkType.CANVAS_AUDIO: {
              // Handled by the persistent connection-level listener.
              return;
            }
            case CanvasChunkType.CANVAS_METRICS: {
              // Handled by the persistent connection-level listener.
              return;
            }
            case CanvasChunkType.CANVAS_CAPTIONS_UPDATE: {
              // Unified token-per-chunk captions. CanvasCaptionsBridge picks this
              // up and re-emits it on the RuntimeContext as CAPTIONS_UPDATE so the
              // tutor's useCaptionsQueue handles it transparently.
              window.dispatchEvent(
                new CustomEvent("canvas:captions-update", { detail: chunk }),
              );
              return;
            }
            case CanvasChunkType.CANVAS_USER_TRANSCRIPT: {
              // Handled by the persistent connection-level listener.
              return;
            }
            case CanvasChunkType.CANVAS_INJECT: {
              // Handled by the persistent connection-level listener.
              return;
            }
            case CanvasChunkType.CANVAS_IMAGE_TRANSCRIPTIONS: {
              // Lets the agent drop image bytes from its persisted chat history.
              window.dispatchEvent(
                new CustomEvent("canvas:image-transcriptions", {
                  detail: {
                    turn_id: chunk.turn_id,
                    chunk_data: chunk.chunk_data,
                  },
                }),
              );
              return;
            }
            default:
              // Forward compatibility: ignore unknown CANVAS_* types
              debugWarn(
                `[WebSocketTransport] Unknown chunk type: ${chunk.chunk_type}`,
              );
              return;
          }
        }
      }
    };

    ws.addEventListener("message", onMessage);

    // Handle abort signal -- send cancel message over WebSocket
    const onAbort = () => {
      if (
        this.ownsConnection() &&
        this.ws === ws &&
        ws.readyState === WebSocket.OPEN
      ) {
        sendCompressed(ws, {
          type: "cancel",
          turn_id: streamTurnId || undefined,
        });
        sendCanvasTurnChangeOnAbort(ws, streamTurnId);
      }
      // Barge-in: the audio these were scheduled against has been cut, so
      // drop them instead of letting them land on top of the next turn.
      clearTimedQueue();
      enqueue({ type: "end" });
    };

    signal.addEventListener("abort", onAbort, { once: true });

    // If already aborted (cancel fired before stream() got here), don't
    // send a prompt the client has already abandoned.
    if (signal.aborted) {
      return;
    }

    // Send the prompt wrapped in Chunk envelopes using the shared turn id.
    const turnId = resolveSharedTurnId();
    streamTurnId = turnId;

    // CANVAS_TEXT: user message for turn tracking and backend prompt assembly.
    // agent_messages preserves the full array (including data:image/ items) so
    // the gateway can reconstruct the exact messages part.
    // user_message contains only the text items — this is what the gateway uses
    // to determine if it's a text turn (non-empty) vs a voice turn (empty,
    // waiting for STT transcript). Image-only items must NOT inflate user_message
    // or the gateway would run the turn before the voice transcript arrives.
    const messagesPart = (
      prompt as Record<string, unknown> & {
        messages?: {
          agentMessages?: string[];
          requestSource?: CanvasTextChunkData["prompt_source"];
        };
      }
    ).messages;
    const agentMessages = messagesPart?.agentMessages ?? [];
    const userMessage = agentMessages
      .filter((m) => !m.startsWith("data:image/"))
      .join("\n");
    if (
      userMessage.trim().length > 0 ||
      agentMessages.some((m) => m.startsWith("data:image/"))
    ) {
      const textChunkData: CanvasTextChunkData = {
        user_message: userMessage,
        agent_messages: agentMessages,
        // Lets the gateway tell a student utterance from a prompt the frontend
        // issued on the agent's behalf (greeting / revisit).
        ...(messagesPart?.requestSource
          ? { prompt_source: messagesPart.requestSource }
          : {}),
      };
      const textChunk = makeCanvasChunk(
        CanvasChunkType.CANVAS_TEXT,
        JSON.stringify(textChunkData),
        turnId,
      );
      this.sendOrQueue(textChunk);
    }

    // CANVAS_CONTEXT: all prompt parts EXCEPT messages
    // Backend synthesizes messages from CANVAS_TEXT (prompt split for voice readiness)
    const { messages: _messages, ...contextOnly } = prompt as Record<
      string,
      unknown
    >;
    const contextChunk = makeCanvasChunk(
      CanvasChunkType.CANVAS_CONTEXT,
      JSON.stringify(contextOnly),
      turnId,
    );
    this.sendOrQueue(contextChunk);

    // 60s timeout between messages — covers both first-response and mid-stream stalls.
    const STREAM_TIMEOUT_MS = 60_000;

    try {
      while (true) {
        await waitForItemWithTimeout(STREAM_TIMEOUT_MS);
        const item = queue.shift();
        if (!item) continue;

        if (item.type === "end") {
          return;
        }
        if (item.type === "error") {
          throw item.error;
        }
        if (item.type === "data") {
          for (const event of item.events) {
            yield event;
          }
        }
      }
    } finally {
      clearTimedQueue();
      ws.removeEventListener("message", onMessage);
      signal.removeEventListener("abort", onAbort);
    }
  }

  // -- Lifecycle -----------------------------------------------------------

  dispose(): void {
    this.disposed = true;
    this.outboundQueue = [];
    // A transport disposed while still deferred would otherwise leave a live
    // store subscription that opens a socket for a torn-down canvas.
    this.releaseSessionWatch();
    this.closeSocket();
    useNotepadStore.getState().releaseConnection(this.owner);
  }
}

// ---------------------------------------------------------------------------
// NoOpTransport
// ---------------------------------------------------------------------------

/**
 * No-operation transport that yields nothing and sends nothing.
 *
 * Used when the canvas agent feature flag is disabled. Silent fail on send —
 * no WebSocket connection is opened.
 */
export class NoOpTransport implements AgentTransport {
  async *stream(
    _prompt: BaseAgentPrompt,
    _signal: AbortSignal,
  ): AsyncGenerator<Streaming<AgentAction>> {
    // yields nothing — silent fail, no WS connection opened
  }
  dispose(): void {}
}
